import { useMemo } from 'react';
import { ArrowRightAlt, Edit, FileCopy } from '@mui/icons-material';
import { Button, Chip, IconButton, Stack } from '@mui/material';
import { DataGridProps, GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { InvoiceStatus, useInvoicesQuery } from '../../api/gql';
import { InvoiceStateBadge } from '../../components/invoice/InvoiceStateBadge';
import { renderCellOrDefault, renderDateOrDefault } from '../../components/table/renderCellOrDefault';
import { Table } from '../../components/table/Table';
import { getInvoiceStateProperties } from '../../utils/invoice';

type Props = { isOverview?: boolean; id?: string } & Partial<DataGridProps>;

export const InvoiceManagementInvoiceTable = ({ isOverview, id = 'invoice-management-invoices', ...rest }: Props) => {
  const { t } = useTranslation();
  const { isLoading, data } = useInvoicesQuery();

  const rows = useMemo(
    () =>
      data?.invoices
        .sort((a, b) => {
          if (a.id < b.id) return 1;
          return -1;
        })
        .map((invoice) => {
          return {
            id: invoice.id,
            number: invoice.number,
            customer: invoice.customer?.name,
            vendor: invoice.vendor?.name,
            settled: invoice.settled,
            issueDate: invoice.issueDate,
            state: invoice.status,
          };
        }) ?? [],
    [data?.invoices],
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'number',
        headerName: t('invoice.number') as string,
        flex: 1,
        minWidth: 100,
        renderCell: renderCellOrDefault,
      },
      {
        field: 'customer',
        headerName: t('customer') as string,
        flex: 1,
        minWidth: 100,
        renderCell: renderCellOrDefault,
      },
      {
        field: 'vendor',
        headerName: t('vendor') as string,
        flex: 1,
        minWidth: 100,
        renderCell: renderCellOrDefault,
      },
      {
        field: 'issueDate',
        headerName: t('issueDate') as string,
        minWidth: 130,
        align: 'right',
        renderCell: renderDateOrDefault,
      },
      {
        field: 'settled',
        headerName: t('status') as string,
        flex: 1,
        minWidth: 100,
        renderCell: (params) => {
          return params.row.settled ? (
            <Chip variant={'outlined'} label={'Uhrazená'} sx={{ borderColor: 'success.main', color: 'success.main' }} />
          ) : (
            <Chip
              variant={'outlined'}
              label={'Vytvořená'}
              sx={{ borderColor: 'secondary.300', color: 'secondary.300' }}
            />
          );
        },
      },
      {
        field: 'state',
        headerName: 'Stav upomínání',
        flex: 1,
        minWidth: 100,
        valueGetter: (params) => {
          // transform value to enable filtering by its translation;
          if (!params.value) {
            return params.value;
          }
          const transKey = getInvoiceStateProperties(params.value, '')?.transKey;
          return t(transKey);
        },
        renderCell: (params) => {
          return [
            InvoiceStatus.Processing,
            InvoiceStatus.MissingRequiredData,
            InvoiceStatus.AwaitingVerification,
          ].includes(params.row.state) ? (
            '-'
          ) : (
            <InvoiceStateBadge state={params.row.state} />
          );
        },
      },
      {
        field: 'action',
        headerName: '',
        filterable: false,
        sortable: false,
        disableColumnMenu: true,
        flex: 1,
        align: 'right',
        minWidth: 450,
        renderCell: (params) => {
          return (
            <Stack direction="row" spacing={1}>
              <IconButton component={Link} to={`/invoice-management/invoices/${params.id}/copy`}>
                <FileCopy color={'primary'} />
              </IconButton>
              {params.row.state !== InvoiceStatus.Reminding ? (
                <IconButton component={Link} to={`/invoice-management/invoices/${params.id}/edit`}>
                  <Edit color={'primary'} />
                </IconButton>
              ) : null}
              <Button component={Link} to={`/invoice-management/invoices/${params.id}`} endIcon={<ArrowRightAlt />}>
                {t('invoiceDetail')}
              </Button>
            </Stack>
          );
        },
      },
    ],
    [t],
  );
  return (
    <Table
      {...rest}
      isOverviewMode={isOverview}
      id={id}
      loading={isLoading}
      autoHeight={true}
      rows={rows}
      columns={columns}
    />
  );
};
